import { Flex, Text } from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingSpinner } from '~/components/shared/LoadingSpinner'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useReleaseNotifications } from '~/hooks/useReleaseNotifications'
import { AnalyticsNamespace, SharedAnalyticsEvents } from '~/types/Analytics'
import { Size } from '~/types/ThemeTypes'
import { isElectron } from '~/utils/common'

import { FeatureFlag } from '@mangovoice/constants'
import { useFeatureFlag } from '@mangovoice/core'
import { textStyleKeys, textStyles } from '@mangovoice/design-system'

import { ReleaseEmailPreview } from './ReleaseEmailPreview'
import {
  SettingLinkAction,
  SettingToggle,
  type SettingToggleRetry,
} from './SettingItems'

export const ReleaseNotifications = () => {
  const { t } = useTranslation()
  const { track } = useAnalytics(AnalyticsNamespace.SHARED)
  const flag = useFeatureFlag(FeatureFlag.ReleaseNotifications)
  const {
    isSubscribed,
    wasUnsubscribed,
    isLoading,
    error,
    isReady,
    recipientEmail,
    setSubscribed,
    refetch,
  } = useReleaseNotifications()
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  // Desktop-only, and dark until the service is deployed + the flag is on.
  if (!isElectron) return null
  if (!flag.isReady || !flag.isEnabled) return null

  const checked = isSubscribed ?? false
  // Mirror the toggle's disabled state here: SettingToggle's disabled prop only
  // dims the switch, so we gate the click ourselves — this also keeps us from
  // emitting a toggle event while a subscribe call is already in flight.
  // `!isReady` covers anything that isn't loadable/toggleable (channel
  // resolution failed, empty-string channel, logged-out, or no account email);
  // without it the toggle would stay clickable and every click would just set
  // error='not_ready' while over-counting the intent event.
  const isToggleDisabled = !isReady || isLoading || isSubscribed === null

  // Still resolving the current subscription (ready + list in flight): show a
  // spinner rather than a definitive OFF toggle, which would briefly mislead an
  // already-subscribed user before their state loads. Anything not-ready
  // (!isReady) falls through to the disabled toggle rather than the spinner, so
  // a non-loadable state can't spin forever; a failed load (error set while
  // isSubscribed is still null) likewise falls through so the toggle can surface
  // the error via alertMessage.
  const isResolving = isReady && isSubscribed === null && !error

  // Recipient line, shown prominently just under the section description (rather
  // than as tiny toggle sub-text, which readers missed): tell people which email
  // release notices go to. Show the concrete address once known; fall back to the
  // generic label while it loads so we never render an empty "Sent to  for…".
  const recipientLine = recipientEmail
    ? t('updateSettings.releaseNotifications.recipientWithEmail', {
        email: recipientEmail,
      })
    : t('updateSettings.releaseNotifications.recipient')

  const handleToggle = () => {
    if (isToggleDisabled) return
    const next = !checked
    // Intent event: fired on the click, not the settled result. An optimistic
    // revert on failure won't retract it — we want to count the attempt, and a
    // failure is surfaced separately through the error alert.
    track(SharedAnalyticsEvents.ReleaseNotificationsToggled, {
      subscribed: next,
    })
    void setSubscribed(next)
  }

  // Retry only the load failure; an update failure is recovered by toggling
  // again, not by re-fetching. Handler + label are supplied together (or not at
  // all) to satisfy SettingToggle's paired retry props.
  const retryProps: SettingToggleRetry =
    error === 'load_failed'
      ? {
          onAlertRetry: () => void refetch(),
          retryLabel: t('updateSettings.releaseNotifications.retry'),
        }
      : {}

  return (
    <Flex direction="column" gap="8px" align="flex-start" width="100%">
      <Text textStyle={'Body2'} fontStyle={'italic'}>
        {recipientLine}
      </Text>
      {isResolving ? (
        <SettingLinkAction
          label={t('updateSettings.releaseNotifications.toggleLabel')}
          controlContent={<LoadingSpinner size={Size.sm} />}
          onClick={() => {}}
        />
      ) : (
        <SettingToggle
          label={t('updateSettings.releaseNotifications.toggleLabel')}
          isChecked={checked}
          disabled={isToggleDisabled}
          onClick={handleToggle}
          alertMessage={
            error
              ? error === 'load_failed'
                ? t('updateSettings.releaseNotifications.loadError')
                : t('updateSettings.releaseNotifications.error')
              : undefined
          }
          {...retryProps}
        />
      )}
      {wasUnsubscribed && !checked && (
        <Text {...textStyles[textStyleKeys.Body4]} color="fg.muted">
          {t('updateSettings.releaseNotifications.resubscribeNote')}
        </Text>
      )}
      <MangoButton
        size={Size.sm}
        variant={MangoButtonVariant.Brand_Text}
        onClick={() => setIsPreviewOpen(true)}
      >
        {t('updateSettings.releaseNotifications.previewButton')}
      </MangoButton>
      <ReleaseEmailPreview
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </Flex>
  )
}
